import { omit, uniq } from 'lodash';
import type { IAddCarePathServiceReaderSettings } from '@common/interfaces/addCarePathServicesSettings';
import {
    type IOrganizationAddCarePathSaveableServiceValidated,
    organizationCarePathAddSchema,
} from '@common/validation/addCarePath';
import { fetchJson } from '@frontend/store/fetch';
import { validate } from '@frontend/utils/validation';
import type {
    IOrganizationAddCarePathState,
    IOrganizationProfile,
} from '../../../../interfaces/organizationAddCarePathState';

function mapStateToSaveObject(state: IOrganizationAddCarePathState) {
    if (!state.episodeCategory || !state.roles || !state.serviceOrganizationSettings || !state.serviceGroups) {
        return;
    }

    const saveObject: {
        episodeCategoryId: string;
        organizationId: string;
        serviceReaderEmploymentIds: string[];
        serviceReaderSettings?: IAddCarePathServiceReaderSettings;
        services: IOrganizationAddCarePathSaveableServiceValidated[];
    } = {
        episodeCategoryId: state.episodeCategory.id,
        organizationId: state.organizationId,
        services: [],
        serviceReaderEmploymentIds: [],
    };

    for (const role of state.roles.filter((r) => r.employments.length > 0)) {
        const serviceGroup = state.serviceGroups.find((group) => group.id === role.serviceGroupId)!;
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        const profile = serviceGroup.profiles?.find((p) => p.id === role.profileId)!;
        const employments = role.employments.map((employment) => ({
            employmentId: employment.id,
            profileId: profile.id,
            episodeCategoryId: state.episodeCategory!.id,
        }));

        const {
            profileConsumerSettings,
            profileProviderSettings,
            profileSupervisorSettings,
            profileReaderSettings,
            profileServiceEmploymentSettings,
        } = getSettingsFromProfile(profile);

        if (profileReaderSettings) {
            saveObject.serviceReaderSettings = profileReaderSettings;
            saveObject.serviceReaderEmploymentIds = uniq([
                ...saveObject.serviceReaderEmploymentIds,
                ...employments.map((o) => o.employmentId),
            ]);
        }

        for (const serviceConsumerSettings of profileConsumerSettings) {
            const service = serviceGroup.services?.find((s) => s.id === serviceConsumerSettings.service.id);
            const serviceOrganizationSettings = state.serviceOrganizationSettings.find(
                (setting) => setting.serviceId === service?.id
            );

            if (serviceOrganizationSettings) {
                saveObject.services.push({
                    ...omit(service, 'name'),
                    serviceConsumerSettings,
                    employments,
                    serviceOrganizationSettings,
                } as IOrganizationAddCarePathSaveableServiceValidated);
            }
        }

        for (const serviceProviderSettings of profileProviderSettings) {
            const service = serviceGroup.services?.find((s) => s.id === serviceProviderSettings.service.id);
            const serviceOrganizationSettings = state.serviceOrganizationSettings.find(
                (setting) => setting.serviceId === service?.id
            );

            if (serviceOrganizationSettings) {
                saveObject.services.push({
                    ...omit(service, 'name'),
                    serviceProviderSettings,
                    employments,
                    serviceOrganizationSettings,
                } as IOrganizationAddCarePathSaveableServiceValidated);
            }
        }

        for (const serviceSupervisorSettings of profileSupervisorSettings) {
            const service = serviceGroup.services?.find((s) => s.id === serviceSupervisorSettings.service.id);
            const serviceOrganizationSettings = state.serviceOrganizationSettings.find(
                (setting) => setting.serviceId === service?.id
            );

            if (serviceOrganizationSettings) {
                saveObject.services.push({
                    ...omit(service, 'name'),
                    serviceSupervisorSettings,
                    employments,
                    serviceOrganizationSettings,
                } as IOrganizationAddCarePathSaveableServiceValidated);
            }
        }

        for (const serviceEmploymentSettings of profileServiceEmploymentSettings) {
            const service = serviceGroup.services?.find((s) => s.id === serviceEmploymentSettings.service.id);
            const serviceOrganizationSettings = state.serviceOrganizationSettings.find(
                (setting) => setting.serviceId === service?.id
            );
            saveObject.services.push({
                ...omit(service, 'name'),
                serviceEmploymentSettings,
                employments,
                serviceOrganizationSettings,
            } as IOrganizationAddCarePathSaveableServiceValidated);
        }
    }

    return saveObject;
}

export function getSettingsFromProfile(profile: IOrganizationProfile) {
    const profileConsumerSettings =
        profile.settings?.serviceConsumerSettings?.filter((settings) => !settings.isDisabled) ?? [];

    const profileProviderSettings =
        profile.settings?.serviceProviderSettings?.filter((settings) => !settings.isDisabled) ?? [];

    const profileSupervisorSettings =
        profile.settings?.serviceSupervisorSettings?.filter((settings) => !settings.isDisabled) ?? [];

    const profileReaderSettings = profile.settings?.serviceReaderSettings?.find((settings) => !settings.isDisabled);

    const profileServiceEmploymentSettings = (
        profile.settings?.serviceEmploymentSettings?.filter((settings) => !settings.isDisabled) ?? []
    ).map((settings) => omit(settings, 'isDisabled'));

    return {
        profileConsumerSettings,
        profileProviderSettings,
        profileSupervisorSettings,
        profileReaderSettings,
        profileServiceEmploymentSettings,
    };
}

export async function saveToApi(organizationId: string, addCarePathState: IOrganizationAddCarePathState) {
    const body = mapStateToSaveObject(addCarePathState);
    const { errors: validationErrors, item } = validate(organizationCarePathAddSchema, body);

    if (validationErrors.size > 0) {
        return { errors: validationErrors };
    } else {
        const url = `/api/organizations/${organizationId}/addCarePath`;
        await fetchJson(url, 'POST', item);

        return 'success';
    }
}
